import { useState, useEffect } from 'react';

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  marketing: boolean;
  analytics: boolean;
  timestamp: string;
}

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    functional: false,
    marketing: false,
    analytics: false
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem('cookie-consent');
      if (stored) {
        const parsed = JSON.parse(stored) as CookiePreferences;
        if (parsed && parsed.timestamp) {
          setIsVisible(false);
          return;
        }
      }
      setIsVisible(true);
    } catch {
      setIsVisible(true);
    }
  }, []);

  const saveConsent = (data: Omit<CookiePreferences, 'timestamp'>) => {
    const consentData: CookiePreferences = {
      ...data,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      functional: true,
      marketing: true,
      analytics: true
    });
  };

  const handleDeny = () => {
    saveConsent({
      essential: true,
      functional: false,
      marketing: false,
      analytics: false
    });
  };

  const handleSaveSettings = () => {
    saveConsent({ ...preferences });
  };

  const togglePreference = (key: 'functional' | 'marketing' | 'analytics') => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] animate-slide-up">
      <div className="bg-white border-t border-gray-200 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">

          {/* Main Banner View */}
          {!showDetails && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-base font-semibold text-[#1E9BD1] mb-2">
                    Privacy Settings
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
                    This site uses third-party website tracking technologies to provide and continually improve our services, and to display advertisements according to users&apos; interests. I agree and may revoke or change my consent at any time with effect for the future.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-800 underline transition-colors"
                  >
                    Privacy Policy
                  </a>
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-800 underline transition-colors"
                  >
                    Terms of Use
                  </a>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="hover:text-gray-800 underline transition-colors cursor-pointer bg-transparent border-none p-0"
                  >
                    More Information
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="flex-1 sm:flex-none bg-gray-100 text-gray-800 py-2.5 px-5 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Save Settings
                  </button>
                  <button
                    onClick={handleDeny}
                    className="flex-1 sm:flex-none bg-gray-100 text-gray-800 py-2.5 px-5 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Deny
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 sm:flex-none bg-[#1E9BD1] text-white py-2.5 px-5 rounded-md text-sm font-medium hover:bg-[#178ABD] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Preferences View */}
          {showDetails && (
            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-[#1E9BD1]">
                  Privacy Settings
                </h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-500 hover:text-gray-800 transition-colors cursor-pointer bg-transparent border-none"
                  aria-label="Close details"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed">
                This site uses third-party website tracking technologies to provide and continually improve our services, and to display advertisements according to users&apos; interests. I agree and may revoke or change my consent at any time with effect for the future.
              </p>

              {/* Toggle Switches */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                {/* Marketing Toggle */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">Marketing</span>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer border-none p-0 ${
                      preferences.marketing ? 'bg-gray-900' : 'bg-gray-300'
                    }`}
                    aria-label="Toggle marketing cookies"
                    aria-pressed={preferences.marketing}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                        preferences.marketing ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Functional Toggle */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">Functional</span>
                  <button
                    onClick={() => togglePreference('functional')}
                    className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer border-none p-0 ${
                      preferences.functional ? 'bg-gray-900' : 'bg-gray-300'
                    }`}
                    aria-label="Toggle functional cookies"
                    aria-pressed={preferences.functional}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                        preferences.functional ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Essential Toggle - always on, disabled */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">Essential</span>
                  <div
                    className="relative w-11 h-6 rounded-full bg-gray-400 cursor-not-allowed"
                    aria-label="Essential cookies always enabled"
                    aria-pressed="true"
                  >
                    <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm translate-x-5" />
                  </div>
                </div>
              </div>

              {/* Bottom Section */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-800 underline transition-colors"
                  >
                    Privacy Policy
                  </a>
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-800 underline transition-colors"
                  >
                    Terms of Use
                  </a>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="hover:text-gray-800 underline transition-colors cursor-pointer bg-transparent border-none p-0"
                  >
                    Less Information
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                  <button
                    onClick={handleSaveSettings}
                    className="flex-1 sm:flex-none bg-[#1E9BD1] text-white py-2.5 px-5 rounded-md text-sm font-medium hover:bg-[#178ABD] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Save Settings
                  </button>
                  <button
                    onClick={handleDeny}
                    className="flex-1 sm:flex-none bg-gray-100 text-gray-800 py-2.5 px-5 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Deny
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 sm:flex-none bg-[#1E9BD1] text-white py-2.5 px-5 rounded-md text-sm font-medium hover:bg-[#178ABD] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}